import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import PublishIcon from '@mui/icons-material/Publish';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import RestoreIcon from '@mui/icons-material/Restore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { AiImageArchiveOutputItem, AiImageGeneration, AiImageOutput } from '../types';
import { copyTextToClipboard, downloadAiImageOutput, getAiImageOutputPreviewUrl } from '../utils';
import { formatImagePixelLabel, getAiImageModelLabel } from '../modelCatalog';
import { getAiImageStatusColor, getAiImageStatusLabel } from '../studioPresentation';
import StudioActionDialog from './StudioActionDialog';

interface ResultsPanelProps {
  generations: AiImageGeneration[];
  archiveOutputs?: AiImageArchiveOutputItem[];
  actionLoading: string | null;
  onApproveRequest: (generationId: string, outputId: string) => void;
  onReject: (generationId: string, outputId: string, reason?: string) => Promise<void>;
  onPublishRequest: (generationId: string, outputId: string) => void;
  onReuse: (generationId: string, outputId: string) => Promise<void>;
  onUsePrompt: (generation: AiImageGeneration) => void;
  onReuseAll: (generation: AiImageGeneration) => void | Promise<void>;
  onUpscale?: (generationId: string, outputId: string) => Promise<void>;
  onOpenRecipe?: (generation: AiImageGeneration) => Promise<void>;
  onEdit?: (generation: AiImageGeneration, output: AiImageOutput) => void;
  onSelectOutput: (generationId: string, outputId: string) => void;
  onDeleteGeneration: (generationId: string) => Promise<void>;
  onLoadMore?: () => Promise<void>;
  archiveHasMore?: boolean;
  archiveLoadingMore?: boolean;
  canReview: boolean;
  canPublish: boolean;
}

interface GalleryItem {
  key: string;
  generation: AiImageGeneration;
  output: AiImageOutput;
}

function aspectRatioFromValue(value?: string): string {
  const ratioMap: Record<string, string> = {
    '1:1': '1 / 1',
    '3:4': '3 / 4',
    '4:3': '4 / 3',
    '2:3': '2 / 3',
    '3:2': '3 / 2',
    '9:16': '9 / 16',
    '16:9': '16 / 9',
    '5:4': '5 / 4',
    '4:5': '4 / 5',
    '21:9': '21 / 9',
  };

  return ratioMap[value || ''] || '4 / 5';
}

function getGalleryItemHeightWeight(item: GalleryItem): number {
  if (item.output.width && item.output.height && item.output.width > 0 && item.output.height > 0) {
    return item.output.height / item.output.width;
  }

  const ratio = aspectRatioFromValue(item.generation.aspectRatio);
  const [widthPart, heightPart] = ratio.split('/').map((part) => Number(part.trim()));

  if (Number.isFinite(widthPart) && Number.isFinite(heightPart) && widthPart > 0 && heightPart > 0) {
    return heightPart / widthPart;
  }

  return 5 / 4;
}

function archiveItemToGeneration(item: AiImageArchiveOutputItem): AiImageGeneration {
  return {
    _id: item.generationId,
    userName: item.userName,
    workflow: item.workflow,
    provider: item.provider,
    model: item.model,
    prompt: item.prompt,
    promptResolved: item.promptResolved,
    presetId: item.presetId,
    presetName: item.presetName,
    promptTemplateId: item.promptTemplateId,
    templatePrompt: item.templatePrompt,
    manualPromptEdited: item.manualPromptEdited,
    targetAssetId: item.targetAssetId,
    editMode: item.editMode,
    editSource: item.editSource,
    editMask: item.editMask,
    preserveOriginalOutsideMask: item.preserveOriginalOutsideMask,
    aspectRatio: item.aspectRatio,
    imageSize: item.imageSize,
    outputCount: item.outputCount,
    status: item.generationStatus,
    references: item.references || [],
    outputs: [item.output],
    events: [],
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  };
}

const archiveIconButtonSx = {
  width: 32,
  height: 32,
  minWidth: 32,
  p: 0,
  flexShrink: 0,
  alignSelf: 'center',
  borderRadius: '50%',
  color: '#fff',
  bgcolor: 'rgba(15,23,42,0.5)',
  border: '1px solid rgba(255,255,255,0.18)',
  backdropFilter: 'blur(8px)',
  transition: 'background-color 0.15s ease, transform 0.15s ease',
  '&:hover': { bgcolor: 'rgba(15,23,42,0.75)', transform: 'translateY(-1px)' },
  '&.Mui-focusVisible': { outline: '2px solid #fff', outlineOffset: 2 },
  '& .MuiSvgIcon-root': { fontSize: 17 },
} as const;

const archiveEditButtonSx = {
  ...archiveIconButtonSx,
  bgcolor: 'rgba(25,118,210,0.7)',
  '&:hover': { bgcolor: 'rgba(25,118,210,0.9)', transform: 'translateY(-1px)' },
} as const;

const archiveApproveButtonSx = {
  ...archiveIconButtonSx,
  bgcolor: 'rgba(46,125,50,0.7)',
  '&:hover': { bgcolor: 'rgba(46,125,50,0.9)', transform: 'translateY(-1px)' },
} as const;

const ResultsPanel: React.FC<ResultsPanelProps> = ({
  generations,
  archiveOutputs,
  actionLoading,
  onApproveRequest,
  onReject,
  onPublishRequest,
  onReuse,
  onUsePrompt,
  onReuseAll,
  onUpscale,
  onOpenRecipe,
  onEdit,
  onSelectOutput,
  onDeleteGeneration,
  onLoadMore,
  archiveHasMore = false,
  archiveLoadingMore = false,
  canReview,
  canPublish,
}) => {
  const theme = useTheme();
  const isXlUp = useMediaQuery(theme.breakpoints.up('xl'));
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuItemKey, setMenuItemKey] = useState<string | null>(null);
  const [deleteGenerationId, setDeleteGenerationId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<{ generationId: string; outputId: string } | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const galleryItems = useMemo<GalleryItem[]>(
    () => {
      if (archiveOutputs) {
        return archiveOutputs.map((item) => ({
          key: `${item.generationId}:${item.outputId}`,
          generation: archiveItemToGeneration(item),
          output: item.output,
        }));
      }

      return generations
        .flatMap((generation) => generation.outputs.map((output) => ({
          key: `${generation._id}:${output._id}`,
          generation,
          output,
        })))
        .sort((left, right) => new Date(right.output.createdAt).getTime() - new Date(left.output.createdAt).getTime());
    },
    [archiveOutputs, generations]
  );

  useEffect(() => {
    if (!onLoadMore || !archiveHasMore || archiveLoadingMore) {
      return;
    }
    const node = loadMoreRef.current;
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        void onLoadMore();
      }
    }, { rootMargin: '700px 0px' });

    observer.observe(node);
    return () => observer.disconnect();
  }, [archiveHasMore, archiveLoadingMore, onLoadMore]);

  const columnCount = isXlUp ? 4 : isLgUp ? 3 : isSmUp ? 2 : 1;

  const galleryColumns = useMemo(() => {
    const columns = Array.from({ length: columnCount }, () => [] as GalleryItem[]);
    const columnHeights = Array.from({ length: columnCount }, () => 0);

    galleryItems.forEach((item, index) => {
      const targetColumnIndex = index < columnCount
        ? index
        : columnHeights.reduce((shortestIndex, currentHeight, currentIndex, heights) => (
          currentHeight < heights[shortestIndex] ? currentIndex : shortestIndex
        ), 0);

      columns[targetColumnIndex].push(item);
      columnHeights[targetColumnIndex] += getGalleryItemHeightWeight(item) + 0.025;
    });

    return columns;
  }, [galleryItems, columnCount]);

  const menuItem = galleryItems.find((item) => item.key === menuItemKey) || null;

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleDownload = async (output: AiImageOutput) => {
    try {
      await downloadAiImageOutput(output);
      setSnackbar({
        open: true,
        message: 'Bilden laddades ned.',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.message || 'Kunde inte ladda ned bilden.',
        severity: 'error',
      });
    }
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, itemKey: string) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuItemKey(itemKey);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuItemKey(null);
  };

  const handleCopyPrompt = async (prompt: string) => {
    try {
      await copyTextToClipboard(prompt);
      setSnackbar({
        open: true,
        message: 'Prompten kopierades.',
        severity: 'success',
      });
      handleCloseMenu();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.message || 'Kunde inte kopiera prompten.',
        severity: 'error',
      });
    }
  };

  return (
    <>
      <Box sx={{ minHeight: 0 }}>
        {galleryItems.length === 0 && (
          <Box
            sx={{
              py: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
              Inga bilder matchar filtret.
            </Typography>
          </Box>
        )}

        {galleryItems.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gap: '6px',
              gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
              alignItems: 'start',
            }}
          >
            {galleryColumns.map((column, columnIndex) => (
              <Box key={`column-${columnIndex}`} sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {column.map((item) => (
                  <Box
                    key={item.key}
                    sx={{
                      cursor: 'pointer',
                      position: 'relative',
                      '&:hover .review-overlay, &:focus-within .review-overlay': { opacity: 1 },
                    }}
                    onClick={() => onSelectOutput(item.generation._id, item.output._id)}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        bgcolor: '#111',
                      }}
                    >
                      <Box
                        component="img"
                        src={getAiImageOutputPreviewUrl(item.output)}
                        alt={item.generation.prompt || 'Genererad bild'}
                        sx={{
                          width: '100%',
                          display: 'block',
                          aspectRatio: item.output.width && item.output.height
                            ? `${item.output.width} / ${item.output.height}`
                            : aspectRatioFromValue(item.generation.aspectRatio),
                          objectFit: 'cover',
                        }}
                      />

                      {/* Comment indicator badge */}
                      {item.output.comments && item.output.comments.length > 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 36,
                            left: 8,
                            zIndex: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.4,
                            bgcolor: '#d32f2f',
                            color: '#fff',
                            borderRadius: 2,
                            height: 26,
                            px: 1,
                            boxShadow: '0 2px 8px rgba(211,47,47,0.45)',
                            pointerEvents: 'none',
                          }}
                        >
                          <ChatBubbleIcon sx={{ fontSize: 13 }} />
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, lineHeight: 1 }}>
                            {item.output.comments.length}
                          </Typography>
                        </Box>
                      )}

                      <Box
                        className="review-overlay"
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          p: 1,
                          background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 40%, transparent 70%, rgba(0,0,0,0.35) 100%)',
                          opacity: { xs: 1, md: 0 },
                          '@media (hover: none)': { opacity: 1 },
                          transition: 'opacity 0.2s ease',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 0.5 }}>
                          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                            <Chip
                              size="small"
                              color={getAiImageStatusColor(item.output.status)}
                              label={getAiImageStatusLabel(item.output.status)}
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: 24,
                              }}
                            />
                            <Chip
                              size="small"
                              label={getAiImageModelLabel(item.generation.model)}
                              sx={{
                                bgcolor: 'rgba(255,255,255,0.15)',
                                backdropFilter: 'blur(8px)',
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: 22,
                              }}
                            />
                            <Chip
                              size="small"
                              label={[
                                item.generation.imageSize,
                                formatImagePixelLabel(item.output.width, item.output.height),
                              ].filter(Boolean).join(' · ')}
                              sx={{
                                bgcolor: 'rgba(255,255,255,0.15)',
                                backdropFilter: 'blur(8px)',
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: 22,
                              }}
                            />
                            {item.output.publishedAssetId && (
                              <Chip
                                size="small"
                                label="Publicerad"
                                sx={{
                                  bgcolor: 'rgba(46,125,50,0.6)',
                                  backdropFilter: 'blur(8px)',
                                  color: '#fff',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  height: 22,
                                }}
                              />
                            )}
                            {item.output.archiveCategory && (
                              <Chip
                                size="small"
                                label={item.output.archiveCategory}
                                sx={{
                                  bgcolor: 'rgba(15,52,96,0.65)',
                                  backdropFilter: 'blur(8px)',
                                  color: '#fff',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  height: 22,
                                }}
                              />
                            )}
                          </Stack>
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="Detaljer">
                              <IconButton
                                aria-label="Detaljer"
                                size="small"
                                sx={archiveIconButtonSx}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onSelectOutput(item.generation._id, item.output._id);
                                }}
                              >
                                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Mer">
                              <IconButton
                                aria-label="Fler åtgärder"
                                size="small"
                                sx={archiveIconButtonSx}
                                onClick={(event) => handleOpenMenu(event, item.key)}
                              >
                                <MoreHorizIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Box>

                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          <Tooltip title="Ladda ned">
                            <IconButton
                              aria-label="Ladda ned"
                              size="small"
                              sx={archiveIconButtonSx}
                              onClick={(event) => {
                                event.stopPropagation();
                                void handleDownload(item.output);
                              }}
                            >
                              <DownloadIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Använd prompt">
                            <IconButton
                              aria-label="Använd prompt"
                              size="small"
                              sx={archiveIconButtonSx}
                              onClick={(event) => {
                                event.stopPropagation();
                                onUsePrompt(item.generation);
                              }}
                            >
                              <TextSnippetIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Använd som referens">
                            <IconButton
                              aria-label="Använd som referens"
                              size="small"
                              sx={archiveIconButtonSx}
                              onClick={(event) => {
                                event.stopPropagation();
                                void onReuse(item.generation._id, item.output._id);
                              }}
                            >
                              <ReplayIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          {onEdit && (
                            <Tooltip title="Redigera">
                              <IconButton
                                aria-label="Redigera"
                                size="small"
                                sx={archiveEditButtonSx}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onEdit(item.generation, item.output);
                                }}
                              >
                                <AutoFixHighIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Återanvänd allt">
                            <IconButton
                              aria-label="Återanvänd allt"
                              size="small"
                              sx={archiveIconButtonSx}
                              onClick={(event) => {
                                event.stopPropagation();
                                onReuseAll(item.generation);
                              }}
                            >
                              <RestoreIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          {onUpscale && (
                            <Tooltip title="Skala upp till 4K">
                              <IconButton
                                aria-label="Skala upp till 4K"
                                size="small"
                                sx={archiveIconButtonSx}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void onUpscale(item.generation._id, item.output._id);
                                }}
                              >
                                <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canPublish && item.output.status === 'completed' && (
                            <Tooltip title="Godkänn">
                              <IconButton
                                aria-label="Godkänn"
                                size="small"
                                sx={archiveApproveButtonSx}
                                disabled={actionLoading === `approve:${item.output._id}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onApproveRequest(item.generation._id, item.output._id);
                                }}
                              >
                                <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        )}

        <Box ref={loadMoreRef} sx={{ height: 1 }} />
        {(archiveLoadingMore || archiveHasMore) && (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center', color: 'text.secondary' }}>
            {archiveLoadingMore ? <CircularProgress size={22} /> : null}
          </Box>
        )}
      </Box>

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl && menuItem)} onClose={handleCloseMenu}>
        <MenuItem
          onClick={() => {
            if (menuItem) {
              onSelectOutput(menuItem.generation._id, menuItem.output._id);
            }
            handleCloseMenu();
          }}
        >
          Visa detaljer
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuItem) {
              void handleCopyPrompt(menuItem.generation.prompt || '');
            }
          }}
        >
          Kopiera prompt
        </MenuItem>
        {menuItem?.generation.workflow === 'recipe_refresh' && menuItem.generation.targetAssetId && onOpenRecipe && (
          <MenuItem
            onClick={async () => {
              await onOpenRecipe(menuItem.generation);
              handleCloseMenu();
            }}
          >
            Visa recept
          </MenuItem>
        )}
        {onEdit && menuItem && (
          <MenuItem
            onClick={() => {
              onEdit(menuItem.generation, menuItem.output);
              handleCloseMenu();
            }}
          >
            <AutoFixHighIcon fontSize="small" sx={{ mr: 1 }} />
            Redigera
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (!menuItem) return;
            setDeleteGenerationId(menuItem.generation._id);
            handleCloseMenu();
          }}
          disabled={!menuItem || actionLoading === `delete:${menuItem.generation._id}`}
        >
          <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />
          Radera generation
        </MenuItem>
        {canReview && menuItem && menuItem.output.status !== 'published_to_dam' && menuItem.output.status !== 'rejected' && (
          <MenuItem
            onClick={() => {
              setRejectTarget({ generationId: menuItem.generation._id, outputId: menuItem.output._id });
              handleCloseMenu();
            }}
          >
            <HighlightOffIcon fontSize="small" sx={{ mr: 1 }} />
            Avslå
          </MenuItem>
        )}
        {canPublish && menuItem?.output.status === 'approved' && (
          <MenuItem
            onClick={() => {
              onPublishRequest(menuItem.generation._id, menuItem.output._id);
              handleCloseMenu();
            }}
          >
            <PublishIcon fontSize="small" sx={{ mr: 1 }} />
            Publicera till DAM
          </MenuItem>
        )}
      </Menu>

      <StudioActionDialog
        open={Boolean(deleteGenerationId)}
        title="Radera generation"
        description="Radera hela generationen från Athena-arkivet? Publicerade DAM-assets påverkas inte. Det här tar bort generationen och dess bilder, inte bara en enstaka bild."
        confirmLabel="Radera generation"
        confirmColor="error"
        onClose={() => setDeleteGenerationId(null)}
        onConfirm={() => {
          if (!deleteGenerationId) return;
          const generationId = deleteGenerationId;
          setDeleteGenerationId(null);
          void onDeleteGeneration(generationId);
        }}
      />
      <StudioActionDialog
        open={Boolean(rejectTarget)}
        title="Avslå bild"
        description="Avslaget sparas på den valda bilden. En orsak är valfri."
        confirmLabel="Avslå"
        confirmColor="error"
        textLabel="Orsak"
        textPlaceholder="Valfritt"
        onClose={() => setRejectTarget(null)}
        onConfirm={(reason) => {
          if (!rejectTarget) return;
          const target = rejectTarget;
          setRejectTarget(null);
          void onReject(target.generationId, target.outputId, reason);
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ResultsPanel;
